import React, { FC } from 'react';

import {
  RoomCancellationPolicyDetailViewModel,
  RoomImageViewModel,
} from '@api/index';
import ListingItemSlider, {
  SliderImage,
} from '@components/ui/listing/ListingItemSlider';
import { Col, Row } from '@components/formStyles';
import CustomIcon from '@components/utils/CustomIcon';
import { AlertIcon, CheckFill, InfoIcon } from '@assets/icons';
import PriceLabel from '@components/ui/labels/price/PriceLabel';
import Button from '@components/Button';
import { getAvailableStatus } from '@utils/utils';
import DiscountLabel from '@components/ui/labels/discount/DiscountLabel';
import MilePriceBadge from '@uikit/milePriceBadge';
import { OrText } from '@uikit/milePriceBadge/styled';

import { DetailedRoomProps } from '../RoomOptions';
import {
  RoomCard,
  Title,
  Subtitle,
  Wrapper,
  PriceCard,
  Line,
  RoomInfo,
  StyledIconLabel,
} from './DetailedRoomStyle';
import { ModalView } from '../details-galleries-modal';
import AvailableRoomDates from '../room-selection/components/AvailableRoomDates';

const FALLBACK_IMAGE = '/static/images/setur-static.webp';

export const formatSliderImage = (
  images: RoomImageViewModel[]
): SliderImage[] =>
  images?.map((image, i) => ({
    imageUrl: image?.url || '',
    alt: `${i + 1}. Oda Görseli`,
    providerId: image?.providerId,
    placeholderUrl: image?.placeholderUrl,
  })) ?? [];
export const cancellationPolicies =
  (policies: RoomCancellationPolicyDetailViewModel[]) => () =>
    (
      <>
        {policies?.map((policy, key) => (
          <li key={key}>{policy.description}</li>
        ))}
      </>
    );
const DetailedRoom: FC<DetailedRoomProps> = ({
  type,
  images,
  features,
  showRoom,
  roomInformation,
  lowestRoomOption,
  status,
  roomId,
  isRefundable,
  cancellationPolicyDetails,
}) => {
  const showModal = () => showRoom(ModalView.VARIATIONS);

  const TooltipText = (props) => {
    const { cancellationPolicyDetails } = props;
    if (!cancellationPolicyDetails?.[0]?.description) {
      return null;
    }
    if (cancellationPolicyDetails?.length) {
      return cancellationPolicies(cancellationPolicyDetails)();
    }
    return null;
  };

  return (
    <>
      <RoomCard>
        <ListingItemSlider
          images={formatSliderImage(images || [])}
          fallBackImage={FALLBACK_IMAGE}
          onGalleryView={showModal}
          onImageClick={showModal}
          width='383px'
          imageStyle={{
            borderRadius: 8,
            borderBottomLeftRadius: 0,
            borderBottomRightRadius: 0,
            height: 200,
          }}
          lazyLoad='ondemand'
        />
        <Wrapper>
          <Row justifyContent='space-between' alignItems='center'>
            <Title>{type}</Title>
            <AvailableRoomDates roomId={roomId} />
          </Row>
          <Col>
            {features?.slice(0, 4).map((feature, i) => (
              <Row key={i}>
                <CustomIcon
                  icon={CheckFill}
                  paletteColor='yellow500'
                  fontSize={16}
                />
                <Subtitle>{feature.name}</Subtitle>
              </Row>
            ))}
          </Col>
        </Wrapper>
      </RoomCard>
      <Line />
      <PriceCard>
        <Row alignItems='center' justifyContent='space-between'>
          <div>
            <RoomInfo>{roomInformation}</RoomInfo>
            <Row style={{ marginTop: 8 }} justifyContent='start'>
              {lowestRoomOption && lowestRoomOption.discountRatioText ? (
                <DiscountLabel
                  type='text'
                  discount={lowestRoomOption.discountRatioText}
                />
              ) : null}
            </Row>
            <div>
              <PriceLabel
                priceWithDiscount={
                  lowestRoomOption?.originalTotalDiscountedSellingPrice
                }
                currency={lowestRoomOption?.currency}
                originalPrice={
                  lowestRoomOption?.discountRatioText &&
                  lowestRoomOption?.originalTotalSellingPrice > 0
                    ? lowestRoomOption.originalTotalSellingPrice
                    : undefined
                }
                sellingPrice={lowestRoomOption?.totalDiscountedSellingPrice}
                style={{ alignItems: 'flex-start' }}
                noSubtitle
              />
              {lowestRoomOption?.milePrice ? (
                <>
                  <OrText> ya da</OrText>
                  <MilePriceBadge milePrice={lowestRoomOption?.milePrice} />
                </>
              ) : null}
            </div>
            <StyledIconLabel
              roomCard={true}
              color={isRefundable ? 'greenInfoDark' : 'redInfoDark'}
              iconColor={isRefundable ? 'greenInfoDark' : 'redInfoDark'}
              backgroundColor={isRefundable ? 'greenInfo' : 'redInfo'}
              text={isRefundable ? 'İptal Edilebilir' : 'İptal Edilemez'}
              icon={isRefundable ? InfoIcon : AlertIcon}
              iconLocation={isRefundable ? 'right' : 'left'}
              iconSize={16}
              tooltipText={
                isRefundable &&
                TooltipText({
                  cancellationPolicyDetails,
                  isRefundable,
                }) ? (
                  <TooltipText
                    cancellationPolicyDetails={cancellationPolicyDetails}
                  />
                ) : null
              }
            />
          </div>
          <Button onClick={showModal}>
            {getAvailableStatus(status).button}
          </Button>
        </Row>
      </PriceCard>
    </>
  );
};

export default DetailedRoom;
